'use client';

import { ArrowCircleUp, Cancel, Collections, Timeline as MuiTimelineIcon } from "@mui/icons-material";
import { Box, Button, Fab, Paper, Stack, styled } from "@mui/material";
import useIsDesktop from "@/hooks/useDesktop";
import { createGlassEffect } from "@/app/theme/theme-extensions";
import type { HistoryTimelineItem } from "@/app/features/sites/history/ui/shared/HistoryTimeline.types";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type { RefObject } from "react";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

const SCALE_MIN = 0.5;
const SCALE_MAX = 1.5;
const SCALE_STEP = 0.125;

interface HistoryTimelinePublicProps {
    items: HistoryTimelineItem[];
}

export default function HistoryTimelinePublic({ items }: HistoryTimelinePublicProps) {
    const isDesktop = useIsDesktop();
    const shouldReduceMotion = useReducedMotion();
    const [currentSectionId, setCurrentSectionId] = useState(items[0]?.id ?? null);
    const currentSectionIdRef = useRef<string | null>(items[0]?.id ?? null);
    const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

    useEffect(() => {
        currentSectionIdRef.current = currentSectionId;
    }, [currentSectionId]);

    useEffect(() => {
        if (typeof IntersectionObserver === "undefined") {
            return;
        }

        const ratios = new Map<string, number>();
        const elements = items
            .map((item) => {
                const element = document.getElementById(item.id);
                if (element) {
                    ratios.set(item.id, 0);
                }

                return { item, element };
            })
            .filter((entry): entry is { item: HistoryTimelineItem; element: HTMLElement } => Boolean(entry.element));

        if (elements.length === 0) {
            return;
        }

        const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 120;
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    const id = entry.target.id;
                    ratios.set(id, entry.isIntersecting ? entry.intersectionRatio : 0);
                }

                let nextSectionId = currentSectionIdRef.current;
                let highestRatio = 0;

                for (const item of items) {
                    const ratio = ratios.get(item.id) ?? 0;
                    if (ratio > highestRatio) {
                        highestRatio = ratio;
                        nextSectionId = item.id;
                    }
                }

                if (nextSectionId && highestRatio > 0 && nextSectionId !== currentSectionIdRef.current) {
                    currentSectionIdRef.current = nextSectionId;
                    setCurrentSectionId(nextSectionId);
                }
            },
            {
                root: null,
                rootMargin: `${-headerHeight}px 0px 0px 0px`,
                threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
            }
        );

        elements.forEach(({ element }) => observer.observe(element));

        return () => {
            observer.disconnect();
        };
    }, [items]);

    const scrollToId = useCallback((sectionId: string) => {
        const element = document.getElementById(sectionId);
        if (!element) {
            return;
        }

        const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        currentSectionIdRef.current = sectionId;
        setCurrentSectionId(sectionId);
        element.scrollIntoView({
            behavior: prefersReducedMotion ? "auto" : "smooth",
            block: "center",
        });

        const heading = element.querySelector<HTMLElement>("h1, h2, h3, h4, h5, h6");
        if (heading) {
            heading.setAttribute("tabindex", "-1");
            window.setTimeout(() => {
                heading.focus({ preventScroll: true });
            }, prefersReducedMotion ? 0 : 350);
        }
    }, []);

    if (items.length === 0) {
        return null;
    }

    return isDesktop
        ? <DesktopVariant currentSectionId={currentSectionId} itemRefs={itemRefs} items={items} scrollToId={scrollToId} shouldReduceMotion={Boolean(shouldReduceMotion)} />
        : <MobileVariant currentSectionId={currentSectionId} itemRefs={itemRefs} items={items} scrollToId={scrollToId} shouldReduceMotion={Boolean(shouldReduceMotion)} />;
}

interface VariantProps {
    currentSectionId: string | null;
    itemRefs: RefObject<(HTMLButtonElement | null)[]>;
    items: HistoryTimelineItem[];
    scrollToId: (sectionId: string) => void;
    shouldReduceMotion: boolean;
}

function DesktopVariant({ currentSectionId, itemRefs, items, scrollToId, shouldReduceMotion }: VariantProps) {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    const selectedIndex = useMemo(() => {
        const index = items.findIndex((item) => item.id === currentSectionId);
        return index >= 0 ? index : 0;
    }, [currentSectionId, items]);

    const getScaleForIndex = useCallback((index: number) => {
        const baseIndex = hoveredIndex ?? selectedIndex;

        if (index === baseIndex) {
            return SCALE_MAX;
        }

        const distance = Math.abs(baseIndex - index) * 1.5;
        return Math.max(SCALE_MIN, SCALE_MAX * Math.exp(-distance * SCALE_STEP));
    }, [hoveredIndex, selectedIndex]);

    return (
        <DesktopVariantContainer role="navigation" aria-label="Zeitleiste">
            <Stack
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    paddingTop: '25vh',
                    paddingBottom: '25vh',
                }}
            >
                {items.map((item, index) => {
                    const isSelected = item.id === currentSectionId;
                    const scale = getScaleForIndex(index);

                    return (
                        <TimelineButton
                            key={item.id}
                            ref={(element) => {
                                itemRefs.current[index] = element;
                            }}
                            type="button"
                            className={isSelected ? 'selected' : ''}
                            aria-current={isSelected ? "location" : undefined}
                            aria-label={item.ariaLabel}
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                            onClick={() => scrollToId(item.id)}
                            style={{
                                fontSize: `${scale * 2}rem`,
                            }}
                        >
                            <SelectedBorder isSelected={isSelected} layoutId="history-public-desktop-border" shouldReduceMotion={shouldReduceMotion} />
                            <ItemContent item={item} />
                        </TimelineButton>
                    );
                })}
            </Stack>
        </DesktopVariantContainer>
    );
}

function MobileVariant({ currentSectionId, itemRefs, items, scrollToId, shouldReduceMotion }: VariantProps) {
    const [isOpen, setIsOpen] = useState(false);
    const panelId = useId();

    const animationVariants = {
        whileOutOfView: {
            opacity: 0,
            right: '-100%',
        },
        whileInView: {
            opacity: 1,
            right: 0,
        }
    };

    const fabAnimationVariants = {
        whileOutOfView: {
            opacity: 0,
        },
        whileInView: {
            opacity: 1,
        }
    };

    function handleItemClick(sectionId: string) {
        setIsOpen(false);
        scrollToId(sectionId);
    }

    return (
        <>
            <AnimatePresence>
                {isOpen && (
                    <MobileVariantContainer
                        id={panelId}
                        aria-label="Zeitleiste"
                        variants={animationVariants}
                        initial="whileOutOfView"
                        animate="whileInView"
                        exit="whileOutOfView"
                        transition={shouldReduceMotion ? { duration: 0 } : undefined}
                        role="navigation"
                    >
                        <Stack
                            spacing={2}
                            sx={{
                                height: '100%',
                            }}
                        >
                            <Stack
                                sx={{
                                    display: 'flex',
                                    flex: '1 1 100%',
                                    minWidth: '100%',
                                    minHeight: 0,
                                    overflow: 'scroll',
                                    scrollbarWidth: 'none',
                                    msOverflowStyle: 'none',
                                    '&::-webkit-scrollbar': {
                                        display: 'none',
                                    }
                                }}
                            >
                                {items.map((item, index) => {
                                    const isSelected = item.id === currentSectionId;

                                    return (
                                        <TimelineButton
                                            key={item.id}
                                            ref={(element) => {
                                                itemRefs.current[index] = element;
                                            }}
                                            type="button"
                                            className={isSelected ? 'selected' : ''}
                                            aria-current={isSelected ? "location" : undefined}
                                            aria-label={item.ariaLabel}
                                            onClick={() => handleItemClick(item.id)}
                                            style={{
                                                fontSize: '2rem',
                                            }}
                                        >
                                            <SelectedBorder isSelected={isSelected} layoutId="history-public-mobile-border" shouldReduceMotion={shouldReduceMotion} />
                                            <ItemContent item={item} />
                                        </TimelineButton>
                                    );
                                })}
                            </Stack>
                            <Button fullWidth onClick={() => setIsOpen(false)} aria-label="Zeitleiste schließen">
                                <Cancel sx={{ fontSize: 36 }} />
                            </Button>
                        </Stack>
                    </MobileVariantContainer>
                )}
            </AnimatePresence>
            <AnimatedFab
                color="primary"
                variants={fabAnimationVariants}
                initial="whileOutOfView"
                animate="whileInView"
                transition={shouldReduceMotion ? { duration: 0 } : undefined}
                sx={{
                    position: 'fixed',
                    right: (theme) => theme.spacing(2),
                    bottom: (theme) => theme.spacing(2),
                    zIndex: 100,
                    visibility: isOpen ? 'hidden' : 'visible',
                }}
                onClick={() => setIsOpen(!isOpen)}
                aria-label="Zeitleiste öffnen"
                aria-controls={panelId}
                aria-expanded={isOpen ? "true" : "false"}
            >
                <MuiTimelineIcon />
            </AnimatedFab>
        </>
    );
}

function SelectedBorder({
    isSelected,
    layoutId,
    shouldReduceMotion,
}: {
    isSelected: boolean;
    layoutId: string;
    shouldReduceMotion: boolean;
}) {
    return (
        <AnimatePresence>
            {isSelected && (
                <motion.div
                    layoutId={layoutId}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: shouldReduceMotion ? 0 : 0.3 }}
                    className="border"
                    layout
                />
            )}
        </AnimatePresence>
    );
}

function ItemContent({ item }: { item: HistoryTimelineItem }) {
    if (item.kind === "intro") {
        return <ArrowCircleUp style={{ fontSize: 42, margin: 16 }} />;
    }

    if (item.kind === "showroom") {
        return <Collections style={{ fontSize: 42, margin: 16 }} />;
    }

    return <>{item.label}</>;
}

const AnimatedFab = motion.create(Fab);
const AnimatedPaper = motion.create(Paper);

const MobileVariantContainer = styled(AnimatedPaper)(({ theme }) => ({
    padding: 2,
    width: '30vw',
    position: 'fixed',
    right: 0,
    bottom: theme.spacing(2),
    top: `calc(var(--header-height) + ${theme.spacing(2)})`,
    borderTopLeftRadius: theme.spacing(1),
    borderBottomLeftRadius: theme.spacing(1),
    borderRightWidth: 0,
    zIndex: 1000,
    ...createGlassEffect(theme),
}));

const DesktopVariantContainer = styled(Box)(() => ({
    position: 'fixed',
    right: '25px',
    top: 'var(--header-height)',
    bottom: '5vh',
    height: '80vh',
    width: '10vw',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 80,
    minWidth: 'fit-content',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    '&::-webkit-scrollbar': {
        display: 'none',
    }
}));

const TimelineButton = styled('button')(() => ({
    appearance: 'none',
    border: 0,
    background: 'transparent',
    cursor: 'pointer',
    transformOrigin: 'right',
    transition: 'font-size 0.3s, margin-top 0.3s, margin-bottom 0.3s, color 0.3s',
    color: 'var(--primary-readable, currentColor)',
    paddingLeft: '8px',
    paddingRight: '8px',
    position: 'relative',
    height: 'auto',
    lineHeight: 1.2,
    font: 'inherit',
    '&.selected': {
        color: 'var(--primary-readable, currentColor)',
        fontWeight: 'bold',
    },
    '&:focus-visible': {
        outline: '2px solid var(--primary-readable, currentColor)',
        outlineOffset: '4px',
    },
    '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
    },
    '.border': {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        border: '2px solid var(--primary-readable, currentColor)',
        borderRadius: '5px',
        pointerEvents: 'none',
    }
}));
